import { useEffect, useState } from "react";
import type { ParkingCollection } from "../lib/parking";
import {
  largestNamedLots,
  modeledParkingOccupancy,
  parkingSummary,
} from "../lib/parking";
import { PanelHeader } from "./PanelHeader";
import { PanelState } from "./PanelState";

/**
 * ParkingPanel — campus parking in two honestly-separated registers:
 *
 *   1. PARKING — MAPPED   Real OSM geometry (© OpenStreetMap, ODbL): counts
 *      of lots / structures / stalls / entrances, tagged capacity coverage,
 *      and the biggest named facilities ranked by computed footprint area.
 *      REF tier chip — the geometry is real, published reference data, never a feed.
 *
 *   2. OCCUPANCY — MODEL  A double-Gaussian commute curve with a visible
 *      MODEL chip. There is NO parking sensor feed yet; the curve exists so
 *      the panel shows what the wired-up version will look like without
 *      pretending the numbers are measured.
 *
 * Self-sufficient: fetches /geo/campus-parking.geojson itself (GistdaPulse
 * pattern — AbortController, never silently disappears).
 */

const PARKING_GEO_URL = "/geo/campus-parking.geojson";
const TOP_LOTS = 5;

type LoadState = "loading" | "ok" | "unavailable";

interface Props {
  /** Override for tests / non-root deployments. */
  geoUrl?: string;
  /** Inject "now" for deterministic tests; defaults to the real clock. */
  now?: Date;
}

/** SVG geometry for the 24 h occupancy curve. */
const OCC_W = 240;
const OCC_H = 64;
const OCC_TOP = 6;
const OCC_BOTTOM = 54;

function occX(hour: number): number {
  return 4 + (hour / 23) * (OCC_W - 8);
}
function occY(pct: number): number {
  return OCC_BOTTOM - (pct / 100) * (OCC_BOTTOM - OCC_TOP);
}

export function ParkingPanel({ geoUrl = PARKING_GEO_URL, now }: Props) {
  const [parking, setParking] = useState<ParkingCollection | null>(null);
  const [state, setState] = useState<LoadState>("loading");

  useEffect(() => {
    const ctrl = new AbortController();
    fetch(geoUrl, { signal: ctrl.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j: ParkingCollection) => {
        const hasData = Boolean(j?.features?.length);
        setParking(hasData ? j : null);
        setState(hasData ? "ok" : "unavailable");
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setState("unavailable");
      });
    return () => ctrl.abort();
  }, [geoUrl]);

  const occ = modeledParkingOccupancy(now ?? new Date());
  const s = parkingSummary(parking);
  const totalLots = s.surfaceLots + s.structures;
  const topLots = largestNamedLots(parking, TOP_LOTS);

  const curvePoints = occ.curve
    .map((pct, h) => `${occX(h).toFixed(1)},${occY(pct).toFixed(1)}`)
    .join(" ");

  return (
    <div className="parking-panel">
      <PanelHeader title="PARKING — MAPPED" source="osm" fallbackTier="reference" />

      {state === "loading" && <PanelState kind="loading" what="parking geometry" />}
      {state === "unavailable" && (
        <PanelState kind="error" what="parking geometry" source="OpenStreetMap snapshot" />
      )}

      {state === "ok" && (
        <>
          <div className="parking-grid">
            <div className="parking-cell">
              <span className="parking-cell-lbl mono">Surface lots</span>
              <span className="parking-cell-val mono">{s.surfaceLots}</span>
              <span className="parking-cell-sub">open-air areas</span>
            </div>
            <div className="parking-cell">
              <span className="parking-cell-lbl mono">Structures</span>
              <span className="parking-cell-val mono">{s.structures}</span>
              <span className="parking-cell-sub">multi-storey / underground</span>
            </div>
            <div className="parking-cell">
              <span className="parking-cell-lbl mono">Mapped stalls</span>
              <span className="parking-cell-val mono">{s.mappedStalls}</span>
              <span className="parking-cell-sub">individual bays drawn</span>
            </div>
            <div className="parking-cell">
              <span className="parking-cell-lbl mono">Entrances</span>
              <span className="parking-cell-val mono">{s.entrances}</span>
              <span className="parking-cell-sub">in / out points</span>
            </div>
          </div>

          <p className="parking-capnote">
            {s.withCapacityTag > 0
              ? `Mapped capacity: ${s.taggedCapacity.toLocaleString()} spaces — ${s.withCapacityTag} of ${totalLots} lots have a capacity tag`
              : `Mapped capacity: none — 0 of ${totalLots} lots carry a capacity tag`}
            {" "}(OSM rarely records it).
          </p>

          {topLots.length > 0 && (
            <div className="parking-lots">
              <span className="eyebrow mono">
                Biggest named lots · by footprint
              </span>
              {topLots.map((lot) => (
                <div key={lot.name} className="parking-lot-row">
                  <span className="parking-lot-name">{lot.name}</span>
                  <span className="parking-lot-meta mono">
                    ≈{Math.round(lot.areaM2).toLocaleString()} m² ·{" "}
                    {lot.capacity != null
                      ? `cap ${lot.capacity.toLocaleString()}`
                      : "cap unmapped"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <section className="parking-occ">
        <header className="parking-occ-head">
          <span className="eyebrow mono">Occupancy — model</span>
          <span className="parking-model-chip mono" title="Modeled double-Gaussian commute curve — no parking sensor feed exists yet">
            MODEL
          </span>
        </header>

        <svg
          className="parking-occ-svg"
          viewBox={`0 0 ${OCC_W} ${OCC_H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`Modeled parking occupancy by hour — now ${occ.currentPct}%`}
        >
          {/* hairline grid: 0 / 50 / 100 % */}
          {[0, 50, 100].map((pct) => (
            <line
              key={pct}
              x1={4}
              x2={OCC_W - 4}
              y1={occY(pct)}
              y2={occY(pct)}
              stroke="var(--line)"
              strokeWidth={1}
            />
          ))}
          {/* dashed = modeled, deliberately not the accent green */}
          <polyline
            points={curvePoints}
            fill="none"
            stroke="var(--text-1)"
            strokeWidth={1.4}
            strokeDasharray="3 2"
          />
          <circle
            cx={occX(occ.hour)}
            cy={occY(occ.currentPct)}
            r={2.6}
            fill="var(--text)"
          />
          {[0, 6, 12, 18].map((h) => (
            <text
              key={h}
              x={occX(h)}
              y={OCC_H - 2}
              fontSize={6}
              fill="var(--text-2)"
              fontFamily="var(--font-mono)"
              textAnchor={h === 0 ? "start" : "middle"}
            >
              {String(h).padStart(2, "0")}
            </text>
          ))}
        </svg>

        <span className="parking-occ-now mono">
          NOW {occ.currentPct}% · {occ.isWeekend ? "weekend" : "weekday"} curve
        </span>

        <p className="parking-foot">
          Occupancy is modeled — no sensor feed yet. Mapped capacity is real
          (OSM). © OpenStreetMap contributors (ODbL).
        </p>
      </section>
    </div>
  );
}
